import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Settings } from 'luxon';
import { formatMinutesIn, localizeMonths, translate, type Language, type Vars } from '@workforce/shared';
import { api } from './api';
import { useAuth } from './auth';

/**
 * English / Arabic for the whole app.
 *
 *   import { t, formatMinutes } from '@/lib/i18n';
 *   t('Save') · t('{n} people', { n: 3 })
 *
 * `t` can be called anywhere during render (not at module top level — that runs once,
 * before the language is known). Switching language remounts the app, so every screen,
 * date (Luxon) and duration is redrawn in the new language and direction.
 */
let current: Language = readStored();

function readStored(): Language {
  try {
    const v = localStorage.getItem('lang');
    if (v === 'ar' || v === 'en') return v;
  } catch {
    /* storage unavailable */
  }
  return 'en';
}

function hasStoredChoice(): boolean {
  try {
    return !!localStorage.getItem('lang');
  } catch {
    return false;
  }
}

function apply(lang: Language) {
  current = lang;
  const html = document.documentElement;
  html.lang = lang;
  html.dir = lang === 'ar' ? 'rtl' : 'ltr';
  // Arabic month and day names, but Western digits (as used in business documents in the region).
  Settings.defaultLocale = lang === 'ar' ? 'ar' : 'en-GB';
  Settings.defaultNumberingSystem = 'latn';
}
apply(current);

export const t = (text: string, vars?: Vars): string => translate(current, text, vars);
export const formatMinutes = (m: number | null | undefined): string => formatMinutesIn(current, m);
export const currentLanguage = (): Language => current;
/** Period names ("September 2026") are stored in English; show the month in the current language. For display only. */
export const periodName = (label: string): string => (current === 'ar' ? localizeMonths(label) : label);
/** Numbers with the right separators for the language (Western digits). */
export const formatNumber = (n: number, opts?: Intl.NumberFormatOptions) =>
  n.toLocaleString(current === 'ar' ? 'ar-u-nu-latn' : 'en-US', opts);

interface I18nState {
  language: Language;
  rtl: boolean;
  setLanguage: (l: Language) => Promise<void>;
}
const Ctx = createContext<I18nState | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user, refresh } = useAuth();
  const [language, setLang] = useState<Language>(current);

  // A signed-in person's saved choice wins over this browser's. Someone who picked a language
  // on the sign-in page and never chose one before keeps it (and it is saved for them).
  useEffect(() => {
    if (!user) return;
    if (!user.languageChosen && hasStoredChoice()) {
      if (current !== user.language) void api.put('/me/language', { language: current }).then(() => refresh()).catch(() => undefined);
      return;
    }
    if (user.language !== language) setLang(user.language);
  }, [user?.language, user?.languageChosen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (current !== language) apply(language);

  const setLanguage = useCallback(
    async (l: Language) => {
      try {
        localStorage.setItem('lang', l);
      } catch {
        /* ignore */
      }
      apply(l);
      setLang(l);
      if (user) {
        await api.put('/me/language', { language: l }).catch(() => undefined);
        await refresh();
      }
    },
    [user, refresh],
  );
  const value = useMemo(() => ({ language, rtl: language === 'ar', setLanguage }), [language, setLanguage]);
  return (
    <Ctx.Provider value={value}>
      <Fragment key={language}>{children}</Fragment>
    </Ctx.Provider>
  );
}

export function useI18n(): I18nState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside I18nProvider');
  return v;
}

/** Small "English | عربي" switch. */
export function LanguageSwitch({ className }: { className?: string }) {
  const { language, setLanguage } = useI18n();
  return (
    <div className={`inline-flex overflow-hidden rounded-lg border border-slate-200 text-xs font-medium ${className ?? ''}`} role="group" aria-label="Language / اللغة">
      {(['en', 'ar'] as const).map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-pressed={language === l}
          onClick={() => language !== l && void setLanguage(l)}
          className={`px-2.5 py-1.5 ${language === l ? 'bg-brand-600 text-white' : 'bg-surface text-slate-600 hover:bg-slate-50'}`}
        >
          {l === 'en' ? 'English' : 'عربي'}
        </button>
      ))}
    </div>
  );
}
